import { Component, ElementRef, inject, OnInit, viewChild } from '@angular/core';
import { DataEntity } from '@barthauer/abc/graphics/adapters/open-layers';
import {
  _AbcGalleryComponent,
  _AbcToolbarComponent,
  AbcLoadPanelComponent,
  AbcPopupComponent,
  AbcDropDownBoxComponent,
  _AbcFormComponent,
  _AbcGridComponent,
} from '@barthauer/abc/ui-components';
import { DxToolbarModule, DxPopupModule, DxFormModule } from 'devextreme-angular';
import { DataService } from '../services/data.service';
import {
  ClickableData,
  LongitudinalSectionPipe,
  LongitudinalSectionSettings,
  LongitudinalSectionSettingsViewModel,
  LongitudinalSectionState,
} from '../models/misc.models';
import { ResourceSet } from '@barthauer/abc/core/resources';
import { PromiseHelper } from '@barthauer/abc/core';
import { CoreService } from 'libs/abc/core/core.service';
import { DataAccessComponent, DataAccessDefinition, ScalableImageComponent } from '@barthauer/basys/graphics/core';

@Component({
  selector: 'lib-basys-graphics-common-longitudinal-section',
  standalone: true,
  imports: [
    _AbcToolbarComponent,
    DxToolbarModule,
    DataAccessComponent,
    ScalableImageComponent,
    AbcLoadPanelComponent,
    AbcPopupComponent,
    AbcDropDownBoxComponent,
    DxPopupModule,
    _AbcFormComponent,
    DxFormModule,
  ],
  templateUrl: './longitudinal-section.component.html',
  styleUrl: './longitudinal-section.component.scss',
})
export class LongitudinalSectionComponent implements OnInit {
  get entities(): DataEntity[] {
    return this._entities;
  }
  set entities(entities: DataEntity[]) {
    if (this._entities !== entities) {
      const isNextPipeSequence = !!this._entities;
      this._entities = entities;

      if (isNextPipeSequence) {
        this.queryGraphics(this.getPipes(entities), true);
      }
    }
  }
  private _entities: DataEntity[];

  resourceSet = ResourceSet.Empty;
  private dataService = inject(DataService);
  private abcCoreService = inject(CoreService);

  private dataAccess = viewChild.required<DataAccessComponent>('dataAccess');
  dataAccessElement = viewChild.required('dataAccess', { read: ElementRef });
  private toolbar = viewChild.required<_AbcToolbarComponent>('toolbar');
  private scalableImage = viewChild.required<ScalableImageComponent<ClickableData>>('scalableImage');

  longitudinalSectionSettings = new LongitudinalSectionSettingsViewModel(new LongitudinalSectionSettings());
  private longitudinalSectionStates: LongitudinalSectionState[] = [];
  private longitudinalSectionState: LongitudinalSectionState;
  private longitudinalSectionPipes: LongitudinalSectionPipe[] = [];
  private currentClickedData?: ClickableData;

  get isLoadPanelVisible(): boolean {
    return (
      this.isQueryGraphicsPending || this.isQueryReportPending
      // || this.isPersistSettingsPending // enable this if persistSettings() should block the user interface
    );
  }
  private isQueryGraphicsPending = true;
  private isQueryReportPending = false;
  private isPersistSettingsPending = false;
  loadPanelPosition: any;

  isSettingsPopupVisible = false;
  private acceptSettingsPopupChanges = false;
  private lastDOMRect: DOMRect;
  private lastWasMaximizedOrUnmaximized: boolean | null = null;

  toolbarRefreshButtonOptions: any;
  toolbarResetZoomButtonOptions: any;
  toolbarPrintButtonOptions: any;
  toolbarSettingsButtonOptions: any;

  settingsPopupAcceptButtonOptions: any;
  settingsPopupCancelButtonOptions: any;

  constructor(private longitudinalSectionComponentElement: ElementRef<HTMLElement>) {}

  async ngOnInit() {
    this.resourceSet = await this.dataAccess().getResourceSet('basys-graphics-common');
    this.initializeComponent(this.resourceSet);
    this.queryGraphics(this.getPipes(this.entities), true);
    const parentPopupWindow = this.longitudinalSectionComponentElement.nativeElement.parentElement;

    if (!parentPopupWindow) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries, observer) => {
      if (!entries.length || !entries[0].target) {
        return;
      }

      if (!this.loadPanelPosition) {
        this.loadPanelPosition = {
          at: 'center',
          my: 'center',
          of: this.dataAccessElement().nativeElement,
        };
      }

      const parentWindow = entries[0].target.parentElement;

      if (!parentWindow) {
        return;
      }

      const parentWidth = entries[0].contentRect.width;
      const parentHeight = entries[0].contentRect.height;
      const toolbarHeight = this.toolbar().instance.element().getBoundingClientRect().height;

      const wasMaximizedOrUnmaximized = this.wasMaximizedOrUnmaximized(parentWindow);
      const scalableImage = this.scalableImage();
      scalableImage.size.set({
        width: parentWidth,
        height: parentHeight - toolbarHeight,
        resetImage:
          wasMaximizedOrUnmaximized || this.lastWasMaximizedOrUnmaximized === false || scalableImage.zoomLevel <= 1.0,
      });
      this.lastWasMaximizedOrUnmaximized = wasMaximizedOrUnmaximized;
    });
    resizeObserver.observe(parentPopupWindow);
  }

  private initializeComponent(resourceSet: ResourceSet): void {
    this.toolbarRefreshButtonOptions = {
      focusStateEnabled: false,
      icon: resourceSet.svgUrl('Refresh-Image'),
      hint: resourceSet.get('Refresh'),
      onClick: () => {
        this.queryGraphics(this.longitudinalSectionPipes, true);
      },
    };
    this.toolbarResetZoomButtonOptions = {
      focusStateEnabled: false,
      icon: resourceSet.svgUrl('ResetZoom-Image'),
      hint: resourceSet.get('ResetZoom'),
      onClick: () => {
        this.scalableImage().resetImage();
      },
    };
    this.toolbarPrintButtonOptions = {
      disabled: false,
      focusStateEnabled: false,
      icon: resourceSet.svgUrl('Print-Image'),
      hint: resourceSet.get('Print'),
      onClick: () => {
        this.queryReport(this.longitudinalSectionPipes, this.longitudinalSectionState.reportFilename);
      },
    };
    this.toolbarSettingsButtonOptions = {
      focusStateEnabled: false,
      icon: resourceSet.svgUrl('Settings-Image'),
      hint: resourceSet.get('Settings'),
      onClick: () => {
        this.acceptSettingsPopupChanges = false;
        this.isSettingsPopupVisible = true;
      },
    };
    this.settingsPopupAcceptButtonOptions = {
      stylingMode: 'outlined',
      text: resourceSet.get('Accept'),
      onClick: () => {
        this.acceptSettingsPopupChanges = true;
        this.isSettingsPopupVisible = false;
      },
    };
    this.settingsPopupCancelButtonOptions = {
      stylingMode: 'outlined',
      text: resourceSet.get('Cancel'),
      onClick: () => {
        this.acceptSettingsPopupChanges = false;
        this.isSettingsPopupVisible = false;
      },
    };
  }

  private queryGraphics(pipes: LongitudinalSectionPipe[], isInitial: boolean): void {
    const currentSettings = this.getSettings();

    if (isInitial && this.longitudinalSectionState) {
      this.currentClickedData = undefined;
      this.scalableImage().clearMarking();
    }

    if (this.longitudinalSectionStates) {
      if (isInitial) {
        this.longitudinalSectionStates.length = 0;
        this.longitudinalSectionStates = [];
      } else {
        const stateMatch = this.longitudinalSectionStates.find((state, index) => {
          return (
            state.verticalScale === currentSettings.verticalScale &&
            state.horizontalScale === currentSettings.horizontalScale
          );
        });

        if (stateMatch) {
          this.setScalableImage(stateMatch);
          this.longitudinalSectionState = stateMatch;
          this.persistSettings(pipes, currentSettings);

          return;
        }
      }
    }

    const pipesId = this.getPipesId(pipes);
    this.dataAccess().callWebApi(
      new DataAccessDefinition(
        'queryGraphics',
        pipesId,
        (isPending) => {
          this.isQueryGraphicsPending = isPending;
        },
        undefined,
        async (data) => {
          if (pipesId !== this.getPipesId(this.entities)) {
            return;
          }

          this.longitudinalSectionPipes = pipes;

          const longitudinalSectionState = new LongitudinalSectionState();
          this.longitudinalSectionState = longitudinalSectionState;
          longitudinalSectionState.reportFilename = data[0].reportFilename;

          if (this.longitudinalSectionStates) {
            this.longitudinalSectionStates.push(longitudinalSectionState);
          }

          if (isInitial) {
            this.longitudinalSectionSettings = new LongitudinalSectionSettingsViewModel(
              new LongitudinalSectionSettings(data[0].settings),
            );
          }

          await PromiseHelper.delay(async () => {
            longitudinalSectionState.imageBase64 = 'data:image/png;base64,' + data[0].imageBase64;
            longitudinalSectionState.imageWidth = data[0].imageWidth;
            longitudinalSectionState.imageHeight = data[0].imageHeight;
            longitudinalSectionState.clickableData = [];

            if (data[0].features) {
              for (const feature of data[0].features) {
                var clickableFeature = new ClickableData(feature);
                longitudinalSectionState.clickableData.push(clickableFeature);
              }
            }

            this.setScalableImage(longitudinalSectionState);
            longitudinalSectionState.verticalScale = this.longitudinalSectionSettings.verticalScale;
            longitudinalSectionState.horizontalScale = this.longitudinalSectionSettings.horizontalScale;
          }, 0);
        },
        [
          () =>
            this.dataService.queryLongitudinalSectionGraphics(
              pipes.map((pipe) => pipe.id),
              pipes.map((pipe) => pipe.objectType.toString()),
              pipes.map((pipe) => pipe.isInFlowDirection),
              currentSettings,
              isInitial,
            ),
        ],
      ),
    );
  }

  private setScalableImage(state: LongitudinalSectionState): void {
    const clickableImage = this.scalableImage();
    clickableImage.clickableFeatures.set(state.clickableData);
    this.currentClickedData = this.currentClickedData?.id
      ? state.clickableData.find((feature) => feature.id === this.currentClickedData.id)
      : undefined;
    clickableImage.image.set({
      imageBase64: state.imageBase64,
      width: state.imageWidth,
      height: state.imageHeight,
      initialMarking: this.currentClickedData,
    });
  }

  private queryReport(pipes: LongitudinalSectionPipe[], reportFilename: string): void {
    const pipesId = this.getPipesId(pipes);
    this.dataAccess().callWebApi(
      new DataAccessDefinition(
        'queryReport',
        pipesId,
        (isPending) => {
          this.isQueryReportPending = isPending;
        },
        (data) => {
          if (pipesId !== this.getPipesId(this.entities)) {
            return;
          }

          if (data[0]?.size > 0) {
            const downloadUrl = URL.createObjectURL(data[0]);
            setTimeout(() => {
              const newWindow = this.abcCoreService.openWindow(downloadUrl);

              if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                const downloadLink = document.children[0].appendChild(document.createElement('a'));
                downloadLink.setAttribute('download', reportFilename);
                downloadLink.setAttribute('href', downloadUrl);
                downloadLink.click();
              }
            }, 100);
          }
        },
        undefined,
        [
          () =>
            this.dataService.queryLongitudinalSectionReport(
              pipes.map((pipe) => pipe.id),
              pipes.map((pipe) => pipe.objectType.toString()),
              pipes.map((pipe) => pipe.isInFlowDirection),
              this.getSettings(),
            ),
        ],
      ),
    );
  }

  private persistSettings(pipes: LongitudinalSectionPipe[], settings?: any): void {
    const pipesId = this.getPipesId(pipes);
    this.dataAccess().callWebApi(
      new DataAccessDefinition(
        'persistSettings',
        pipesId,
        (isPending) => {
          this.isPersistSettingsPending = isPending;
        },
        (data) => {
          if (pipesId !== this.getPipesId(this.entities)) {
            return;
          }
        },
        undefined,
        [
          () =>
            this.dataService.persistLongitudinalSectionSettings(
              pipes[0].id,
              pipes[0].objectType.toString(),
              pipes[0].isInFlowDirection,
              settings ?? this.getSettings(),
            ),
        ],
      ),
    );
  }

  private getSettings(): any {
    const settings: any = {};
    settings.graphicsDocumentId = this.longitudinalSectionSettings.graphicsDocumentId;
    settings.reportDocumentId = this.longitudinalSectionSettings.reportDocumentId;
    settings.verticalScale = this.longitudinalSectionSettings.verticalScale;
    settings.horizontalScale = this.longitudinalSectionSettings.horizontalScale;

    return settings;
  }

  private getPipes(entities: DataEntity[]): LongitudinalSectionPipe[] {
    return entities.map((entity) => {
      const pipe = new LongitudinalSectionPipe();
      pipe.id = entity.id;
      pipe.objectType = entity.objectType;
      pipe.isInFlowDirection = entity.isInFlowDirection ?? true;

      return pipe;
    });
  }

  private getPipesId(pipes: DataEntity[] | LongitudinalSectionPipe[]): string {
    let pipesId = pipes[0].id;

    for (let i = 1; i < pipes.length; i++) {
      pipesId += '|' + pipes[i].id;
    }

    return pipesId;
  }

  handleSettingsPopupHidden(): void {
    if (!this.acceptSettingsPopupChanges || !this.longitudinalSectionSettings.hasChanges) {
      this.longitudinalSectionSettings.reset();
      return;
    }

    let isInitial = null;

    if (this.longitudinalSectionSettings.graphicsDocumentIdHasChanged) {
      isInitial = true;
    } else if (
      this.longitudinalSectionSettings.verticalScaleHasChanged ||
      this.longitudinalSectionSettings.horizontalScaleHasChanged
    ) {
      isInitial = false;
    }

    this.longitudinalSectionSettings.accept();

    if (isInitial === null) {
      this.persistSettings(this.longitudinalSectionPipes);
    } else {
      this.queryGraphics(this.longitudinalSectionPipes, isInitial);
    }
  }

  handleFeatureClicked(clickedData: ClickableData | undefined): void {
    this.currentClickedData = clickedData;
  }

  private wasMaximizedOrUnmaximized(element: Element): boolean | null {
    const tolerance = 10;
    const domRect = element.getBoundingClientRect();

    try {
      if (this.lastDOMRect) {
        if (
          domRect.width <= window.innerWidth &&
          window.innerWidth - domRect.width < tolerance &&
          domRect.height <= window.innerHeight &&
          window.innerHeight - domRect.height < tolerance &&
          domRect.width > this.lastDOMRect.width + tolerance &&
          domRect.height > this.lastDOMRect.height + tolerance
        ) {
          return true; // was maximized
        } else if (
          this.lastDOMRect.width <= window.innerWidth &&
          window.innerWidth - this.lastDOMRect.width < tolerance &&
          this.lastDOMRect.height <= window.innerHeight &&
          window.innerHeight - this.lastDOMRect.height < tolerance &&
          domRect.width < this.lastDOMRect.width - tolerance &&
          domRect.height < this.lastDOMRect.height - tolerance
        ) {
          return false; // was unmaximized
        }
      }

      return null;
    } finally {
      this.lastDOMRect = domRect;
    }
  }

  getSettingsPopupWidth(): string {
    if (window.innerWidth <= 768) {
      return '90%';
    } else if (window.innerWidth <= 1024) {
      return '80%';
    }
    return '400px';
  }

  getSettingsPopupHeight(): string {
    if (window.innerWidth <= 768) {
      return '90%';
    } else if (window.innerWidth <= 1024) {
      return '80%';
    }
    return '300px';
  }
}
